/**
 * S4 (VIEW) — the specialist workspace navigator.
 *
 * Reads lexserver slice 3 (`1da68cd19`): tree, file, per-file history. This is
 * a PLATFORM-OWNED share reached through lexserver's own API — NOT the runtime
 * fs/git proxy `RuntimeFsBrowser` uses. Rendering idiom is borrowed from that
 * browser; transport is not, which is the distinction that let this section be
 * planned before the API existed.
 *
 * Three rules this file exists to hold:
 *
 * 1. **A refusal is not an absence.** Every failure lexserver returns is a
 *    typed code, and several share an HTTP status while meaning different
 *    things to an operator. They are rendered per-node and per-pane, never
 *    collapsed into an empty tree — the failure mode this lane shipped once on
 *    the Agent Bus tab and does not intend to ship twice.
 * 2. **An oversized file is REFUSED, never truncated.** The server returns no
 *    content at all over 512 KiB. Saying "too large" beside a blank pane would
 *    let an operator read it as a truncated head — which becomes a live hazard
 *    the moment the write half lands and someone saves what they are looking at.
 * 3. **No edit affordance may exist outside the tier table.** `lib/specialist-
 *    tier.ts` is the only authority, and it is gated twice (tier + write-API
 *    availability), so the write half lands INTO a rule instead of authoring one.
 *
 * History is METADATA ONLY by lexserver's deliberate design — sha, date,
 * subject, no diff, no blob. Revert is forward-apply (`Lex-Reverts:<sha>`), so
 * nothing here needs historical content, and withholding it keeps a line
 * removed by a later commit from being retrievable through this UI.
 *
 * That left a real gap — forward-apply revert would ask an operator to revert
 * BLIND, since "seed the domain reference bundle" could be nine files or one
 * line. Raised, and lexserver shipped the compromise rather than the diff: a
 * per-commit STAT (`…/commit?sha=`, `865349001`) — paths and `+N/-M` counts,
 * never content. A count is not a line, so §6.4's property survives whole.
 * ⚠ One widening to know rather than infer: a commit that DELETED a file
 * reveals that file's PATH, which the tree no longer shows. Deliberate on their
 * side — it is often the most decision-relevant fact about a revert.
 */

import {
  ChevronDown,
  ChevronRight,
  File as FileIcon,
  Folder,
  FolderOpen,
  GitCommit,
  Lock,
  RefreshCw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { probe, type Probe } from "@/lib/probe";
import { browsableRoster, selectHomeView } from "@/lib/specialist-home-state";
import { mayEdit, tierFor } from "@/lib/specialist-tier";
import {
  describeSpecialistsError,
  getSpecialistCommitStat,
  getSpecialistFile,
  getSpecialistHistory,
  getSpecialistHomeState,
  getSpecialistTree,
  SpecialistsApiError,
  type SpecialistCommit,
  type SpecialistCommitStat,
  type SpecialistFileContent,
  type SpecialistHomeState,
  type SpecialistTreeEntry,
} from "@/lib/specialists-api";

/** One sentence for any thrown failure — typed refusal or transport fault. */
function sentence(e: unknown): string {
  if (e instanceof SpecialistsApiError) return describeSpecialistsError(e);
  return e instanceof Error ? e.message : String(e ?? "unknown failure");
}

/**
 * Settle a read the way `lib/probe.ts` does, but formatting the failure through
 * `sentence()`.
 *
 * `probe()` keeps `e.message`, which for a typed refusal is the server's text —
 * good, but it drops exactly the consequence this surface has to state: that an
 * oversized file was refused WHOLE rather than truncated, and that a path
 * refusal is not an absence. Same settled discipline, domain-aware wording.
 */
async function settle<T>(p: Promise<T>): Promise<Probe<T>> {
  try {
    return { ok: true, value: await p };
  } catch (e) {
    return { ok: false, error: sentence(e) };
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

// ── Tree ──────────────────────────────────────────────────────────────────

interface TreeLevelProps {
  path: string;
  depth: number;
  listings: Map<string, Probe<SpecialistTreeEntry[]>>;
  expanded: Set<string>;
  loading: Set<string>;
  selected: string | null;
  onToggle: (path: string) => void;
  onOpen: (entry: SpecialistTreeEntry) => void;
}

function TreeLevel(props: TreeLevelProps) {
  const { path, depth, listings, expanded, loading, selected, onToggle, onOpen } = props;
  const result = listings.get(path);
  const pad = { paddingLeft: `${depth * 14 + 8}px` };

  if (!result) {
    return loading.has(path) ? (
      <div style={pad} className="py-1 text-xs text-slate-400">
        Loading…
      </div>
    ) : null;
  }

  if (!result.ok) {
    // A directory that could not be read renders AS a failed read, in place —
    // not as an empty folder, and not by blanking the whole tree.
    return (
      <div
        style={pad}
        className="py-1 text-xs text-amber-600 dark:text-amber-500"
        data-testid={`workspace-dir-error-${path || "root"}`}
      >
        Could not read this folder — {result.error}
      </div>
    );
  }

  if (result.value.length === 0) {
    return (
      <div style={pad} className="py-1 text-xs text-slate-400">
        empty folder
      </div>
    );
  }

  return (
    <>
      {result.value.map((entry) => {
        const isDir = entry.type === "dir";
        const open = expanded.has(entry.path);
        const tier = tierFor(entry.path);
        return (
          <div key={entry.path}>
            <button
              type="button"
              style={pad}
              onClick={() => (isDir ? onToggle(entry.path) : onOpen(entry))}
              title={tier.note}
              className={`flex w-full items-center gap-1.5 py-1 pr-2 text-left text-xs transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 ${
                selected === entry.path
                  ? "bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                  : "text-slate-700 dark:text-slate-300"
              }`}
              data-testid={`workspace-entry-${entry.path}`}
            >
              {isDir ? (
                <>
                  {open ? (
                    <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
                  )}
                  {open ? (
                    <FolderOpen className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                  ) : (
                    <Folder className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                  )}
                </>
              ) : (
                <>
                  <span className="w-3 shrink-0" />
                  <FileIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                </>
              )}
              <span className="truncate">{entry.name}</span>
              {!isDir && entry.size !== undefined && (
                <span className="ml-auto shrink-0 tabular-nums text-[10px] text-slate-400">
                  {formatBytes(entry.size)}
                </span>
              )}
            </button>
            {isDir && open && <TreeLevel {...props} path={entry.path} depth={depth + 1} />}
          </div>
        );
      })}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function SpecialistWorkspace() {
  const [home, setHome] = useState<Probe<{ home: SpecialistHomeState }> | null>(null);
  const [specialistId, setSpecialistId] = useState<string | null>(null);

  const [listings, setListings] = useState<Map<string, Probe<SpecialistTreeEntry[]>>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());

  const [selected, setSelected] = useState<string | null>(null);
  const [file, setFile] = useState<SpecialistFileContent | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const [history, setHistory] = useState<SpecialistCommit[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [openSha, setOpenSha] = useState<string | null>(null);
  const [stat, setStat] = useState<Probe<SpecialistCommitStat> | null>(null);

  // Monotonic race token. Clicking file A then file B while A is in flight must
  // not paint A's content under B's name — the reads are click-retargetable and
  // the server has no idea which one the operator is still looking at.
  const openSeq = useRef(0);

  const loadHome = useCallback(async () => {
    setHome(await probe(getSpecialistHomeState()));
  }, []);

  useEffect(() => {
    void loadHome();
  }, [loadHome]);

  const homeView = useMemo(() => selectHomeView(home), [home]);
  const roster = browsableRoster(homeView);

  // Pick the first specialist once the roster is readable; never guess an id
  // when it is not.
  useEffect(() => {
    if (specialistId === null && roster && roster.length > 0) setSpecialistId(roster[0]);
  }, [roster, specialistId]);

  const loadDir = useCallback(
    async (id: string, path: string) => {
      setLoadingPaths((p) => new Set(p).add(path));
      const result = await settle(getSpecialistTree(id, path).then((r) => r.entries));
      setListings((prev) => new Map(prev).set(path, result));
      setLoadingPaths((p) => {
        const n = new Set(p);
        n.delete(path);
        return n;
      });
    },
    [],
  );

  // Reset every derived view when the specialist changes — a stale tree under a
  // new name is worse than an empty one.
  useEffect(() => {
    setListings(new Map());
    setExpanded(new Set());
    setSelected(null);
    setFile(null);
    setFileError(null);
    setHistory(null);
    setHistoryError(null);
    setOpenSha(null);
    setStat(null);
    openSeq.current += 1;
    if (specialistId) void loadDir(specialistId, "");
  }, [specialistId, loadDir]);

  const toggleFolder = useCallback(
    (path: string) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(path)) next.delete(path);
        else {
          next.add(path);
          if (!listings.has(path) && specialistId) void loadDir(specialistId, path);
        }
        return next;
      });
    },
    [listings, loadDir, specialistId],
  );

  const openFile = useCallback(
    async (entry: SpecialistTreeEntry) => {
      if (!specialistId) return;
      const seq = ++openSeq.current;
      setSelected(entry.path);
      setFile(null);
      setFileError(null);
      setHistory(null);
      setHistoryError(null);
      setOpenSha(null);
      setStat(null);

      // Client-side refusal for anything the API never serves, so a restored or
      // typed path never becomes a request. The server refuses too — this is
      // the near half of a defence in depth, not a substitute for it.
      const tier = tierFor(entry.path);
      if (tier.neverServed) {
        setFileError(tier.note);
        return;
      }

      setLoadingFile(true);
      const [f, h] = await Promise.all([
        settle(getSpecialistFile(specialistId, entry.path)),
        settle(getSpecialistHistory(specialistId, entry.path)),
      ]);
      // A response for a file the operator has already navigated away from is
      // discarded whole — including its errors.
      if (seq !== openSeq.current) return;

      if (f.ok) setFile(f.value);
      else setFileError(f.error);
      if (h.ok) setHistory(h.value.commits);
      else setHistoryError(h.error);
      setLoadingFile(false);
    },
    [specialistId],
  );

  const toggleStat = useCallback(
    async (sha: string) => {
      if (!specialistId) return;
      if (openSha === sha) {
        setOpenSha(null);
        setStat(null);
        return;
      }
      setOpenSha(sha);
      setStat(null);
      const seq = ++openSeq.current;
      const r = await settle(getSpecialistCommitStat(specialistId, sha));
      // Shares the file-open race token deliberately: navigating to another file
      // must discard an in-flight stat for the previous one's history.
      if (seq !== openSeq.current) return;
      setStat(r);
    },
    [specialistId, openSha],
  );

  const selectedTier = selected ? tierFor(selected) : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="specialist-workspace">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <label className="text-xs font-medium text-slate-500 dark:text-slate-400" htmlFor="sp-pick">
          Specialist
        </label>
        <select
          id="sp-pick"
          value={specialistId ?? ""}
          onChange={(e) => setSpecialistId(e.target.value || null)}
          disabled={!roster || roster.length === 0}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 font-mono text-xs disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
          data-testid="workspace-specialist-picker"
        >
          {(roster ?? []).map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            void loadHome();
            if (specialistId) {
              setListings(new Map());
              setExpanded(new Set());
              void loadDir(specialistId, "");
            }
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* The roster is unreadable, or the home is not ready. Say WHICH — an
          empty picker beside a silent page is the exact ambiguity this section
          exists to refuse. */}
      {roster === null && (
        <div
          className="shrink-0 rounded-md bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
          data-testid="workspace-no-roster"
        >
          {homeView.kind === "unread"
            ? `Could not read the specialist home — ${homeView.reason}. This is NOT “there are no workspaces”; see Health.`
            : `The specialist home is ${homeView.kind === "unrecognized" ? homeView.state : homeView.kind}, so no workspace can be browsed. See Health for what that means.`}
        </div>
      )}
      {roster !== null && roster.length === 0 && (
        <div
          className="shrink-0 rounded-md bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300"
          data-testid="workspace-empty-roster"
        >
          The home is ready and holds no specialist workspaces.
        </div>
      )}

      {specialistId && (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,320px)_1fr]">
          <Card className="flex min-h-0 flex-col overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {specialistId}
              </CardTitle>
            </CardHeader>
            <CardContent className="min-h-0 flex-1 overflow-auto p-0 pb-2">
              <TreeLevel
                path=""
                depth={0}
                listings={listings}
                expanded={expanded}
                loading={loadingPaths}
                selected={selected}
                onToggle={toggleFolder}
                onOpen={openFile}
              />
            </CardContent>
          </Card>

          <div className="flex min-h-0 flex-col gap-3">
            <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground">
                  <span className="font-mono">{selected ?? "no file selected"}</span>
                  {selectedTier && (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                      title={selectedTier.note}
                      data-testid="workspace-tier-badge"
                    >
                      <Lock className="h-2.5 w-2.5" />
                      {selectedTier.tier}
                      {/* Read-only is stated, not implied by the absence of a
                          button — an operator should not have to infer it. */}
                      {!mayEdit(selected ?? "") && " · read-only"}
                    </span>
                  )}
                  {file && (
                    <span className="ml-auto shrink-0 text-[11px] font-normal tabular-nums text-slate-400">
                      {formatBytes(file.size)} · {new Date(file.modifiedAt).toLocaleString()}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="min-h-0 flex-1 overflow-auto">
                {!selected && (
                  <p className="py-10 text-center text-sm text-slate-400">
                    Select a file to read it. This surface is read-only — the kb write half is not
                    live yet.
                  </p>
                )}
                {loadingFile && <p className="py-10 text-center text-sm text-slate-400">Reading…</p>}
                {fileError && (
                  <div
                    className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
                    data-testid="workspace-file-error"
                  >
                    {fileError}
                  </div>
                )}
                {file && !loadingFile && (
                  <pre
                    className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-slate-700 dark:text-slate-300"
                    data-testid="workspace-file-content"
                  >
                    {file.content}
                  </pre>
                )}
              </CardContent>
            </Card>

            {selected && (
              <Card className="max-h-64 shrink-0 overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <GitCommit className="h-4 w-4" />
                    History
                    <span className="text-[11px] font-normal text-slate-400">
                      click a commit for its stat — paths and counts, never content
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="max-h-44 overflow-auto pt-0">
                  {historyError && (
                    <div
                      className="rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
                      data-testid="workspace-history-error"
                    >
                      Could not read history — {historyError}
                    </div>
                  )}
                  {history !== null && history.length === 0 && (
                    <p className="py-3 text-xs text-slate-400">
                      No commits touch this path — it is untracked or newly written.
                    </p>
                  )}
                  {history !== null && history.length > 0 && (
                    <ul className="flex flex-col gap-1.5" data-testid="workspace-history">
                      {history.map((c) => (
                        <li key={c.sha} className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => void toggleStat(c.sha)}
                            className="flex w-full items-baseline gap-2 rounded px-1 py-0.5 text-left text-xs transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                            title="Show what this commit changed — paths and line counts, never content."
                            data-testid={`workspace-commit-${c.sha.slice(0, 8)}`}
                          >
                            <span className="shrink-0 font-mono text-slate-400">
                              {c.sha.slice(0, 8)}
                            </span>
                            <span className="shrink-0 tabular-nums text-slate-400">
                              {new Date(c.authoredAt).toLocaleDateString()}
                            </span>
                            <span className="min-w-0 truncate text-slate-700 dark:text-slate-300">
                              {c.subject}
                            </span>
                          </button>
                          {openSha === c.sha && (
                            <div className="ml-2 border-l border-slate-200 pl-3 dark:border-slate-700">
                              {stat === null && (
                                <p className="py-1 text-[11px] text-slate-400">Reading…</p>
                              )}
                              {stat && !stat.ok && (
                                <p
                                  className="py-1 text-[11px] text-amber-600 dark:text-amber-500"
                                  data-testid="workspace-stat-error"
                                >
                                  Could not read this commit — {stat.error}
                                </p>
                              )}
                              {stat?.ok && (
                                <div data-testid="workspace-stat">
                                  {stat.value.files.length === 0 && (
                                    <p className="py-1 text-[11px] text-slate-400">
                                      This commit changed no files under this specialist.
                                    </p>
                                  )}
                                  {stat.value.files.map((f) => (
                                    <div
                                      key={f.path}
                                      className="flex items-baseline gap-2 py-0.5 text-[11px]"
                                    >
                                      <span className="min-w-0 flex-1 truncate font-mono text-slate-600 dark:text-slate-400">
                                        {f.path}
                                      </span>
                                      {/* null = BINARY (git reports `-`), which
                                          is not zero. Rendering it as 0 would
                                          claim a file did not change. */}
                                      {f.added === null && f.removed === null ? (
                                        <span className="shrink-0 text-slate-400">binary</span>
                                      ) : (
                                        <>
                                          <span className="shrink-0 tabular-nums text-emerald-600 dark:text-emerald-400">
                                            +{f.added ?? "?"}
                                          </span>
                                          <span className="shrink-0 tabular-nums text-red-600 dark:text-red-400">
                                            −{f.removed ?? "?"}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
